import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _isNil from "lodash/isNil";
import _get from "lodash/get";

var _FDIC_TYPE_ANNOTATION;

import { getLogger } from '@nerdwallet/logging';
import { BANKING_SUB_PRODUCT_TYPES, FDIC_ANNOTATION, FEDERALLY_INSURED_TYPES, LEGACY_TO_CANONICAL_INSTITUTION_TYPES, MONETIZATION_STATUSES, NCUA_ANNOTATION, NON_MEMBER_FDIC_ANNOTATION, PRODUCT_TYPES } from '../constants';
import { getAdditionalData } from '../utils';
import { makeModuleDeprecationDecorator } from '../utils/deprecation';
import { callSelectorBySubProductType, getInstitution, getProduct, getCanonicalInstitutionType } from './common/general-utils';
var withDeprecationWarning = makeModuleDeprecationDecorator('banking');
var logger = getLogger('@nerdwallet/marketplace/selectors/banking');
/**
 * Gets annual percentage yield.
 *
 * @param      {Object}  marketplaceEntity  The BNK entity.
 *
 * @return     {string}                     The annual percentage yield.
 */

export var getAPY = function getAPY(marketplaceEntity) {
  return _get(marketplaceEntity, 'derived_details.display_apy.value');
};
/**
 * Gets APY annotation text.
 *
 * @param      {Object}  marketplaceEntity  The BNK entity.
 *
 * @return     {string}                     The APY annotation text.
 */

export var getAPYAnnotation = function getAPYAnnotation(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.apy_annotation.value');
};
/**
 * Gets minimum balance needed to avoid fees.
 *
 * @param      {Object}  marketplaceEntity  The BNK entity.
 *
 * @return     {number}                     The minimum balance needed to avoid fees.
 */

export var getBalanceToAvoidFees = function getBalanceToAvoidFees(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.balance_to_avoid_fees.value');
};
/**
 * Gets bonus amount received for opening an account.
 *
 * @param      {Object}  marketplaceEntity  The BNK entity.
 *
 * @return     {number}                     The bonus amount received.
 */

export var getBonusAmount = function getBonusAmount(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.bonus_amount.value');
};
/**
 * Gets details about the bonus terms and conditions.
 *
 * @param      {Object}  marketplaceEntity  The BNK entity.
 *
 * @return     {number}                     The bonus details.
 */

export var getBonusDetails = function getBonusDetails(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.bonus_details.value');
};
/**
 * Get bottom line content.
 * @note expects augmentation via `banking-products` post type through
 * AdditionalDataSDK.
 * Fallback to `getBottomLineInstitution`.
 *
 * @param  {Object} marketplaceEntity     The (banking-products augmented) BNK entity
 *
 * @return {Object|String}             "Bottom line" Structured Content or text
 */

export var getBottomLine = function getBottomLine(marketplaceEntity) {
  var fallbackBottomLine = getBottomLineInstitution(marketplaceEntity);
  return _get(getAdditionalData(getProduct(marketplaceEntity)), 'summary', fallbackBottomLine);
};
/**
 * Get bottom line content on "Bank experience".
 * @note expects augmentation via `banking-institutions` post type through
 * AdditionalDataSDK
 *
 * @param {Object}  marketplaceEntity  The BNK entity.
 *
 * @return {Object}                   "Bank experience bottom line" Structured Content
 */

export var getBottomLineBankExperience = function getBottomLineBankExperience(marketplaceEntity) {
  return _get(getAdditionalData(getInstitution(marketplaceEntity)), 'bank_experience_summary');
};
/**
 * Get bottom line content for the institution.
 * @note expects augmentation via `banking-institutions` post type through
 * AdditionalDataSDK
 * Fallback to deprecated field set by banking-products service.
 *
 * @param {Object}  marketplaceEntity  The BNK entity.
 *
 * @return {Object|String}          "Institution bottom line" Structured Content or text
 */

export var getBottomLineInstitution = function getBottomLineInstitution(marketplaceEntity) {
  var fallbackBottomLine = _get(marketplaceEntity, 'review');

  return _get(getAdditionalData(getInstitution(marketplaceEntity)), 'summary', fallbackBottomLine);
};
/**
 * Get bottom line content on "Overdraft fee".
 * @note expects augmentation via `banking-institutions` post type through
 * AdditionalDataSDK
 *
 * @param {Object}  marketplaceEntity  The BNK entity.
 *
 * @return {Object}                    "Overdraft fee bottom line" Structured Content
 */

export var getBottomLineOverdraftFee = function getBottomLineOverdraftFee(marketplaceEntity) {
  return _get(getAdditionalData(getInstitution(marketplaceEntity)), 'overdraft_fee_summary');
};
/**
 * Get the "cons" bullets for an offer.
 * @note expects augmentation via `banking-products` post type through
 * AdditionalDataSDK
 *
 * @param   {Object} marketplaceEntity  The BNK entity.
 * @return  {Array<Object>} - The "cons" Structured Content array
 */

export var getConsBullets = function getConsBullets(marketplaceEntity) {
  return _get(getAdditionalData(getProduct(marketplaceEntity)), 'cons');
};
/**
 * Gets direct deposit needed to avoid fees.
 *
 * @param      {Object}  marketplaceEntity  The BNK entity.
 *
 * @return     {number}                     The value of a direct deposit needed to avoid fees.
 */

export var getDirectDepositWaive = function getDirectDepositWaive(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.direct_deposit_waive.value');
};
/**
 * Gets display pages of a marketplaceEntity
 *
 * @param      {Object}  marketplaceEntity  The BNK entity.
 *
 * @return     {Array}                      The array of display pages
 */

export var getDisplayPages = function getDisplayPages(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.display_pages.value', []);
};
/**
 * Gets display status of a marketplaceEntity
 *
 * @param      {Object}  marketplaceEntity  The BNK entity.
 *
 * @return     {string}                     The status [ACTIVE,INACTIVE]
 */

export var getDisplayStatus = function getDisplayStatus(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.display_status.value');
};
/**
 * Gets agency name that federally insures the entity's institution.
 *
 * @param      {Object}  marketplaceEntity  The BNK entity.
 *
 * @return     {string}                     The agency name that federally insures
 *                                          the entity's institution.
 */

export var getFederallyInsured = function getFederallyInsured(marketplaceEntity) {
  return _get(getInstitution(marketplaceEntity), 'details.federally_insured.value');
};
var FDIC_TYPE_ANNOTATIONS = (_FDIC_TYPE_ANNOTATION = {}, _defineProperty(_FDIC_TYPE_ANNOTATION, FEDERALLY_INSURED_TYPES.FDIC, FDIC_ANNOTATION), _defineProperty(_FDIC_TYPE_ANNOTATION, FEDERALLY_INSURED_TYPES.NCUA, NCUA_ANNOTATION), _defineProperty(_FDIC_TYPE_ANNOTATION, FEDERALLY_INSURED_TYPES.NON_MEMBER_FDIC, NON_MEMBER_FDIC_ANNOTATION), _FDIC_TYPE_ANNOTATION);
/**
 * Get annotation for the agency that federally insures the institution.
 *
 * @param {Object} marketplaceEntity The BNK entity.
 * @return {string} The annotation to be displayed about the insuring agency.
 */

export var getFederallyInsuredAnnotation = function getFederallyInsuredAnnotation(marketplaceEntity) {
  var federallyInsured = getFederallyInsured(marketplaceEntity);
  return FDIC_TYPE_ANNOTATIONS[federallyInsured];
};
/**
 * Gets the name of the entity as the alt text for the associated image
 *
 * @param     {Object} marketplaceEntity  The marketplace entity
 * @return    {string}                    Alt text
 */

export var getImageAltText = function getImageAltText(marketplaceEntity) {
  return getProductName(marketplaceEntity);
};
/**
 * Gets source URL of the image associated with the offer.
 *
 * @param      {Object}  marketplaceEntity  The banking entity
 *
 * @return     {string}                     The URL of the image.
 */

export var getImageSrc = function getImageSrc(marketplaceEntity) {
  return _get(getInstitution(marketplaceEntity), 'details.logo_url.value');
};
/**
 * Get the issuer.
 *
 * @param {Object} marketplaceEntity The marketplace entity.
 *
 * @return {string} Issuer name.
 */

export var getIssuerName = function getIssuerName(marketplaceEntity) {
  return _get(getInstitution(marketplaceEntity), 'details.display_name.value');
};
/**
 * Gets url to review.
 *
 * @param  {Object} marketplaceEntity The marketplace entity
 *
 * @return {String}                   url
 */

export var getLearnMoreLink = function getLearnMoreLink(marketplaceEntity) {
  return _get(getInstitution(marketplaceEntity), 'details.nw_review_url.value');
};
/**
 * Get the ingested, legacy banking institution type.
 * @deprecated - Use the `getInstitutionType` selector instead.
 *
 * @param {Object} marketplaceEntity - The marketplace entity.
 * @returns {String} - The legacy, bank institution type.
 */

export var getLegacyInstitutionType = function getLegacyInstitutionType(marketplaceEntity) {
  return _get(getInstitution(marketplaceEntity), 'details.type.value');
};
/**
 * Get the canonical, banking institution type.
 * Currently the legacy banking field is more reliably accurate so grab both and warn of mismatch.
 * @see {@link https://nerdwallet.slack.com/archives/CFUL4319S/p1597877978230300?thread_ts=1597874030.229700&cid=CFUL4319S}
 */

export var getInstitutionType = function getInstitutionType(marketplaceEntity) {
  var legacyType = getLegacyInstitutionType(marketplaceEntity);
  var convertedType = LEGACY_TO_CANONICAL_INSTITUTION_TYPES[legacyType];
  var canonicalType = getCanonicalInstitutionType(marketplaceEntity);

  if (convertedType !== canonicalType) {
    logger.warn("getInstitutionType() mismatch: `institution.type` \"".concat(canonicalType, "\" and `institution.details.type` \"").concat(legacyType, "\" -> \"").concat(convertedType, "\" "));
  }

  return convertedType || canonicalType;
};
/**
 * Gets required minimum balance.
 *
 * @param      {Object}  marketplaceEntity  The BNK entity.
 *
 * @return     {number}                     The required minimum balance.
 */

export var getMinimumBalance = function getMinimumBalance(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.minimum_balance.value');
};
/**
 * Gets monthly fee.
 *
 * @param      {Object}  marketplaceEntity  The BNK entity.
 *
 * @return     {number}                     The monthly fee.
 */

export var getMonthlyFee = function getMonthlyFee(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.fee_monthly.value');
};
/**
 * Gets name to display for an offer.
 * @note banking client shows generic name derived from sub-product type based on
 * simple heuristic if name is non-semantic
 *  https://github.com/NerdWallet/banking-client/blob/4423d3790f67e8f61f68a889a51079a753c40a24/client/components/results/results-utils.js#L87-L100
 *  https://github.com/NerdWallet/banking-client/blob/ac7ec2a2de9280d11aa8b69cf9e98554c9912414/client/lib/reducers/map-offers.js#L14-L20
 * @deprecated since 1.66.0 - Use "getProductName" directly.
 *
 * @param {Object} marketplaceEntity - The banking entity.
 * @return {String} - The offer name.
 */

export var getName = withDeprecationWarning(function (marketplaceEntity) {
  var _callSelectorBySubPro;

  var issuerName = getIssuerName(marketplaceEntity);
  var productName = getProductName(marketplaceEntity);
  return callSelectorBySubProductType(marketplaceEntity, (_callSelectorBySubPro = {}, _defineProperty(_callSelectorBySubPro, BANKING_SUB_PRODUCT_TYPES.CHECKING, function () {
    return "".concat(issuerName, " ").concat(productName);
  }), _defineProperty(_callSelectorBySubPro, BANKING_SUB_PRODUCT_TYPES.SAVINGS, function () {
    return "".concat(issuerName, " ").concat(productName);
  }), _defineProperty(_callSelectorBySubPro, BANKING_SUB_PRODUCT_TYPES.CASH_MANAGEMENT, function () {
    return productName;
  }), _defineProperty(_callSelectorBySubPro, BANKING_SUB_PRODUCT_TYPES.MONEY_MARKET, function () {
    return productName;
  }), _defineProperty(_callSelectorBySubPro, BANKING_SUB_PRODUCT_TYPES.CD, function () {
    return "".concat(issuerName, " CD");
  }), _callSelectorBySubPro), _defineProperty({}, PRODUCT_TYPES.BANKING, getSubProductType)) || issuerName;
}, {
  name: 'getName',
  migration: 'Use "banking/getProductName" directly.',
  version: '1.66.0'
});
/**
 * Get the offer url.
 *
 * @param      {Object}  marketplaceEntity  The BNK entity.
 *
 * @return     {String}                     The offer url.
 */

export var getOfferUrl = function getOfferUrl(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.offer_url.value');
};
/**
 * Gets out-of-network ATM fee.
 *
 * @param      {Object}  marketplaceEntity  The BNK entity.
 *
 * @return     {number}                     The out-of-network ATM fee.
 */

export var getOutOfNetworkATMFee = function getOutOfNetworkATMFee(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.fee_atm.value');
};
/**
 * Get the product name.
 *
 * @param       {Object} marketplaceEntity The marketplace entity.
 *
 * @return      {string} Product name.
 */

export var getProductName = function getProductName(marketplaceEntity) {
  return _get(getProduct(marketplaceEntity), 'details.display_name.value');
};
/**
 * Get the "pros" bullets for an offer.
 * @note expects augmentation via `banking-products` post type through
 * AdditionalDataSDK
 *
 * @param   {Object} marketplaceEntity  The BNK entity.
 * @return  {Array<Object>} - The "pros" Structured Content array
 */

export var getProsBullets = function getProsBullets(marketplaceEntity) {
  return _get(getAdditionalData(getProduct(marketplaceEntity)), 'pros');
};
/**
 * Get the institution rating for "Bank experience".
 *
 * @param  {Object} marketplaceEntity  The BNK entity.
 *
 * @return {Number}                    The institution rating for "Bank experience".
 */

export var getRatingBankExperience = function getRatingBankExperience(marketplaceEntity) {
  return _get(getInstitution(marketplaceEntity), 'details.nw_rating_bank_experience.value');
};
/**
 * Get the institution rating.
 *
 * @param  {Object} marketplaceEntity  The BNK entity.
 *
 * @return {Number}                    The institution rating.
 */

export var getRatingInstitution = function getRatingInstitution(marketplaceEntity) {
  return _get(getInstitution(marketplaceEntity), 'details.nw_rating.value');
};
/**
 * Get the institution rating for "Overdraft fee".
 *
 * @param  {Object} marketplaceEntity  The BNK entity.
 *
 * @return {Number}                    The institution rating for "Overdraft fee".
 */

export var getRatingOverdraftFee = function getRatingOverdraftFee(marketplaceEntity) {
  return _get(getInstitution(marketplaceEntity), 'details.nw_rating_overdraft_fee.value');
};
/**
 * Get whether this offer could monetize.
 * It relies on offer.details.is_monetizing (which does not exist at the time of writing), and will fall back to offer.derived_details.is_monetizing (which does exist).
 * This derived detail field (offer.derived_details.is_monetizing) is not consumed automatically and must be explicitly requested.
 *
 * @param {Object} marketplaceEntity    The banking entity.
 *
 * @return {boolean}                    Whether the offer could monetize. Defaults to true.
 * @deprecated - opt for common#getMonetizable
 */

export var couldMonetize = function couldMonetize(marketplaceEntity) {
  var isMonetizing = _get(marketplaceEntity, 'details.is_monetizing.value');

  return _isNil(isMonetizing) ? true : isMonetizing;
};
/**
 * Get whether this offer should monetize.
 * This derived detail field (offer.derived_details.should_throttle_monetization) is not consumed automatically and must be explicitly requested.
 *
 * @param {Object} marketplaceEntity    The banking entity.
 *
 * @return {boolean}                    Whether the offer should monetize. Defaults to true.
 * @deprecated - opt for common#getMonetizable
 */

export var shouldMonetize = function shouldMonetize(marketplaceEntity) {
  var shouldThrottle = _get(marketplaceEntity, 'derived_details.should_throttle_monetization.value', false);

  return !shouldThrottle;
};
/**
 * Get this offer's monetization status as a string ('yes_assumed' | 'no' | 'no_because_of_limit' | 'no_because_of_lead').
 * This derived detail field (offer.derived_details.monetizable) is not consumed automatically and must be explicitly requested.
 *
 * @param {Object} marketplaceEntity    The banking entity.
 *
 * @return {string}                    Whether the offer should monetize ('yes_assumed' | 'no' | 'no_because_of_limit' | 'no_because_of_lead'). Defaults to 'yes_assumed'.
 * @deprecated - opt for common#getMonetizable, all banking offers with ID return a value
 */

export var getMonetizationStatus = function getMonetizationStatus(marketplaceEntity) {
  var monetizationStatus = _get(marketplaceEntity, 'derived_details.monetizable.value');

  return _isNil(monetizationStatus) ? MONETIZATION_STATUSES.YES_ASSUMED : monetizationStatus;
};
/**
 * Get whether this offer is monetizing.
 * @note - This function is kept for backwards compatibility. It has been succeded by `shouldMonetize` and `couldMonetize` which provide improved clarity.
 * @note - Fallback to deprecated field set by banking-products service.
 * This derived detail field is not consumed automatically and must be explicitly requested.
 *
 * @param {Object} marketplaceEntity    The banking entity.
 *
 * @return {boolean}                    Whether the offer is monetizing.
 */

export var getIsMonetizing = shouldMonetize;
/**
 * Get slug.
 *
 * @param {Object} marketplaceEntity The banking entity.
 *
 * @return {string} The slug.
 */

export var getSlug = function getSlug(marketplaceEntity) {
  var institutionName = getIssuerName(marketplaceEntity);

  var productName = _get(getProduct(marketplaceEntity), 'details.display_name.value');

  return "".concat(institutionName, "-").concat(productName);
};
/**
 * Gets star rating.
 * @note - fallback to deprecated review rating set by banking-products service
 * or the institution rating (same path).
 *
 * @param      {Object}  marketplaceEntity  The banking entity.
 *
 * @return     {number}                     The star rating.
 */

export var getStarRating = function getStarRating(marketplaceEntity) {
  return _get(getProduct(marketplaceEntity), 'details.rating_stars.value') || getRatingInstitution(marketplaceEntity);
};
/**
 * Gets star rating tooltip.
 *
 * @return     {string}                     The star rating tooltip.
 */

export var getStarRatingTooltip = function getStarRatingTooltip() {
  return "NerdWallet's ratings are determined by our editorial team. The scoring formulas take into account multiple data points for each financial product and service.";
};
/**
 * Get sub product type.
 *
 * @param {Object} marketplaceEntity The BNK entity.
 *
 * @return {number} The sub product type.
 */

export var getSubProductType = function getSubProductType(marketplaceEntity) {
  return _get(getProduct(marketplaceEntity), 'details.sub_product_type.value');
};
/**
 * Get sub copy for the monthly fee
 *
 * @param {Object} marketplaceEntity The BNK offer entity.
 *
 * @return {String|null} The sub copy for the monthly fee.ß
 */

export var getMonthlyFeeSubCopy = function getMonthlyFeeSubCopy(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.fee_monthly_sub_copy.value');
};
/**
 * Get minimum opening deposit
 *
 * @param {Object} marketplaceEntity The BNK offer entity.
 *
 * @return {number|null} The minimum opening deposit value.
 */

export var getMinimumOpeningDeposit = function getMinimumOpeningDeposit(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.min_opening_deposit.value');
};
/**
 * Get Product Audience.
 *
 * @param {Object} marketplaceEntity The BNK entity.
 *
 * @return {String} The product audience.
 */

export var getProductAudience = function getProductAudience(marketplaceEntity) {
  return _get(getProduct(marketplaceEntity), 'details.product_audience.value');
};